import { PlayerBrowserSupport } from "./player_browser_support";
import { PlayerEnvironment } from "./player_environment";
import { Timing } from "./utils/timing";
import AhoyAdapter from "./adapters/ahoy_adapter";
import VideoAdapter from "./adapters/video_adapter";
import MSEAdapter from "./adapters/mse_adapter";
import VideoJsAdapter from "./adapters/videojs_adapter";

export class PlayerAhoyEvent {

  static env(){
    return Object.assign({
      player_name: "videojs",
      player_version: VideoJsAdapter.version()
    },PlayerEnvironment.currentEnv());
  }

  static autoplayAttribute(autoplay){
    return {
      autoplay: autoplay.value(),
      autoplay_data: autoplay.data(),
    };
  }

  static globalAppendData(){
    return Object.assign({}, this._globalAppendData) || {};
  }

  static appendGlobalProperties(properties){
    return Object.assign(this.globalAppendData(), properties);
  }

  static sendEvent(type, properties, prefix="VIDEO::"){
    let eventProperties = this.appendGlobalProperties(properties)
    AhoyAdapter.track(prefix + type, eventProperties);
  }

  static sendPlayerEvent({prefix, type, properties}){
    const props = Object.assign(this.env(), properties);
    if(prefix) {
      this.sendEvent(prefix + type, props, '');
    } else {
      this.sendEvent(type, props);
    }
  }

  static sendBlocker(content_type, is_premier, content_id, blocker_type){
    const props = Object.assign({
      content_type,
      is_premier,
      content_id,
      blocker_type
    }, this.env());

    this.sendEvent('BLOCKER', props, 'PLAYBACK::')
  }

  static sendSkipIntro(action, content_id, play_uuid){
    const props = Object.assign({
      action,
      feature: 'skip intro',
      content_id,
      content_type: 'vod',
      play_uuid,
    });

    this.sendEvent('SKIP', props, 'PLAYBACK::')
  }

  static sendPreviewSubscribe(action, duration){
    const props = Object.assign({
      action,
      feature: 'preview button',
      duration
    });

    this.sendEvent('SUBSCRIPTION', props, 'VIDIO::')
  }

  static sendKeyPressed(key, play_uuid, long_press) {
    const props = Object.assign({
      key,
      play_uuid,
      long_press
    }, this.env());

   this.sendEvent('KEYPRESSED', props, 'PLAYBACK::' );
  }

  static sendContinueWatch(video_id) {
    const props = Object.assign({
      video_id,
    }, this.env());

    this.sendEvent('CONTINUE', props, 'PLAYBACK::');
  }

  static sendPause(video_id, play_uuid, genre_id, user_triggered) {
    user_triggered = user_triggered || false;
    const props = Object.assign({
      video_id,
      play_uuid,
      genre_id,
      user_triggered
    }, this.env());

    this.sendEvent('PAUSE', props);
  }

  static sendResume(video_id, play_uuid) {
    const props = Object.assign({
      video_id,
      play_uuid
    }, this.env());

    this.sendEvent('RESUME', props);
  }

  static sendStart(
    videoId,
    videoDuration,
    hasAd,
    autoplay,
    playUUID,
    genreIds,
    currentTime,
    readyState,
    networkState,
    bufferedVideoFrames,
    tokenRefreshCount,
    bandwidth,
    quality,
    codec,
    is_drm,
    hdcp_support,
    videoSourceBitrate,
    videoSourceHeight,
    videoSourceWidth,
    playerHeight,
    playerWidth
  ) {
    const env = this.env();
    window.playback.startTime = env.event_time;

    var properties = Object.assign({
      video_id: videoId,
      video_duration: videoDuration,
      setup_time: Timing.time("vjs-setup"),
      has_ad: hasAd,
      play_uuid: playUUID,
      visitor_id: AhoyAdapter.global().visitorId,
      genre_id: genreIds,
      position: currentTime,
      ready_state: readyState,
      network_state: networkState,
      buffered_video_frames: bufferedVideoFrames,
      is_tokenized: true,
      token_refresh_count: tokenRefreshCount,
      vjs_html5: VideoJsAdapter.isHtml5Supported(),
      vjs_mpegurl: VideoJsAdapter.isMpegUrlSupported(),
      has_adblocker: PlayerEnvironment.hasAdBlocker(),
      bandwidth,
      quality,
      after_retry: window.playback.restartCount > 0,
      codec: codec,
      is_drm: is_drm,
      hdcp_support,
      video_start_time: ( window.playback.startTime || null ),
      video_source_bitrate: videoSourceBitrate,
      video_source_height: videoSourceHeight,
      video_source_width: videoSourceWidth,
      player_height: playerHeight,
      player_width: playerWidth
    }, this.autoplayAttribute(autoplay), env);

    this.sendEvent("START", properties, 'PLAYBACK::');
    this.sendEvent("START", properties);
  }

  static sendBuffer(video_id, seconds, play_uuid, genre_id, position, timer, bandwidth, quality, video_source_bitrate, video_source_height, video_source_width, player_height, player_width) {
    const props = Object.assign({
      video_id,
      seconds,
      play_uuid,
      genre_id,
      position,
      timer,
      bandwidth,
      quality,
      video_source_bitrate,
      video_source_height,
      video_source_width,
      player_height,
      player_width
    }, this.env());

    this.sendEvent('BUFFER', props);
  }

  static sendPlaybackBuffer({ buffer_id, play_uuid, content_type, content_id, is_premium, state, quality, bandwidth, is_preview, codec, video_source_bitrate, video_source_height, video_source_width, player_height, player_width } = {}) {
    const props = Object.assign({
      buffer_id,
      play_uuid,
      content_type,
      content_id,
      is_premium,
      state,
      quality,
      bandwidth,
      is_preview,
      codec,
      video_source_bitrate,
      video_source_height,
      video_source_width,
      player_height,
      player_width
    }, this.env());
    this.sendEvent('BUFFER', props, 'PLAYBACK::');
  }

  static sendSeek(video_id, position, offset, play_uuid, action) {
    const props = Object.assign({
      content_id: video_id,
      content_type: 'vod',
      position,
      offset,
      play_uuid,
      action
    }, this.env());

    this.sendEvent('SEEK', props, 'PLAYBACK::');
  }

  static sendSetupError(videoId, message, hasAd, playUUID, tokenRefreshCount){
    var properties = Object.assign({
      video_id: videoId,
      message: message,
      has_ad: hasAd,
      play_uuid: playUUID,
      is_tokenized: true,
      token_refresh_count: tokenRefreshCount
    }, this.env());

    this.sendEvent("SETUP ERROR", properties);
  }

  static sendWatch(
    video_id,
    percentage,
    play_uuid,
    bandwidth,
    quality,
    network_state,
    has_ad,
    video_duration,
    autoplay,
    videoSourceBitrate,
    videoSourceHeight,
    videoSourceWidth,
    playerHeight,
    playerWidth
  ) {
    const props = Object.assign({
      video_id,
      percentage,
      play_uuid,
      bandwidth,
      quality,
      network_state,
      has_ad,
      video_duration,
      video_start_time: ( window.playback.startTime || null ),
      restart_count: ( window.playback.restartCount || 0),
      video_start_time: ( window.playback.startTime || null ),
      video_source_bitrate: videoSourceBitrate,
      video_source_height: videoSourceHeight,
      video_source_width: videoSourceWidth,
      player_height: playerHeight,
      player_width: playerWidth
    }, this.autoplayAttribute(autoplay), this.env());

    this.sendEvent("WATCH", props);
  }

  static sendError(videoId, code, message, videoDuration, currentTime, playUUID, readyState, networkState, bufferedVideoFrames, tokenRefreshCount){
    var properties = Object.assign({
      video_id: videoId,
      error_code: code,
      message: message,
      video_duration: videoDuration,
      current_time: currentTime,
      play_uuid: playUUID,
      ready_state: readyState,
      network_state: networkState,
      buffered_video_frames: bufferedVideoFrames,
      is_tokenized: true,
      token_refresh_count: tokenRefreshCount,
      vjs_html5: VideoJsAdapter.isHtml5Supported(),
      vjs_mpegurl: VideoJsAdapter.isMpegUrlSupported(),
    }, this.env());

    this.sendEvent("ERROR", properties);
  }

  static sendAdsRequest(props) {
    const properties = Object.assign({
      content_id: props.content_id,
      ad_uuid: props.ad_uuid,
      ad_tag: props.ad_tag,
      videopremier: props.videopremier,
      ad_source: props.ad_source,
      play_uuid: props.play_uuid,
      content_type: props.content_type
    }, this.env());

    this.sendEvent('AD::REQUEST', properties, 'PLAYBACK::');
  }

  static sendAdRuleRequest(props) {
    const properties = Object.assign({
      content_id: props.content_id,
      ad_tag: props.ad_tag,
      videopremier: props.videopremier,
      ad_source: props.ad_source,
      play_uuid: props.play_uuid,
      content_type: props.content_type,
      ad_playlist_id: props.ad_playlist_id
    }, this.env());

    this.sendEvent('AD_RULE::REQUEST', properties, 'PLAYBACK::');
  }

  static sendAdLoaded(options) {
    const properties = Object.assign(options, this.env());

    this.sendEvent('AD::LOADED', properties, 'PLAYBACK::');
  }

  static sendAdStart(props) {
    var properties = Object.assign({
      content_id: props.content_id,
      content_type: props.content_type,
      ad_uuid: props.ad_uuid,
      ad_content_type: props.ad_content_type,
      ad_playlist_id: props.ad_playlist_id,
      current_time: props.current_time,
      play_uuid: props.play_uuid
    }, this.env());

    this.sendEvent("AD::START", properties, 'PLAYBACK::');
  }

  static sendAdError(props) {
    const properties = Object.assign({
      play_uuid: props.play_uuid,
      content_id: props.content_id,
      content_type: props.content_type,
      video_duration: props.video_duration,
      current_time: props.current_time,
      ad_uuid: props.ad_uuid,
      ad_tag: props.ad_tag,
      error_code: props.error_code,
      message: props.message,
      error_type: props.error_type
    }, this.env());

    this.sendEvent('AD::ERROR', properties, 'PLAYBACK::');
  }

  static sendAdClick(props) {
    const properties = Object.assign({
      content_id: props.content_id,
      content_type: props.content_type,
      position: props.position,
      play_uuid: props.play_uuid,
      ad_uuid: props.ad_uuid
    }, this.env());

    this.sendEvent('AD::CLICK', properties, 'PLAYBACK::');
  }

  static sendAdComplete(props) {
    const properties = Object.assign({
      content_id: props.content_id,
      content_type: props.content_type,
      position: props.position,
      play_uuid: props.play_uuid,
      ad_uuid: props.ad_uuid
    }, this.env());

    this.sendEvent("AD::COMPLETE", properties, 'PLAYBACK::');
  }

  static sendAdSkipped(props) {
    const properties = Object.assign({
      content_id: props.content_id,
      content_type: props.content_type,
      position: props.position,
      play_uuid: props.play_uuid,
      ad_uuid: props.ad_uuid
    }, this.env());

    this.sendEvent('AD::SKIPPED', properties, 'PLAYBACK::');
  }

  static sendAdWatch(video_id, duration, seconds, play_uuid, ad_uuid, ad_type) {
    const props = Object.assign({
      video_id,
      duration,
      seconds,
      play_uuid,
      ad_uuid,
      ad_type
    }, this.env());

    this.sendEvent("AD::WATCH", props);
  }

  static sendAdFirstQuartile(props) {
    const properties = Object.assign({
      play_uuid: props.play_uuid,
      content_id: props.content_id,
      content_type: props.content_type,
      ad_uuid: props.ad_uuid,
      ad_tag: props.ad_tag
    }, this.env());

    this.sendEvent('AD::FIRST_QUARTILE', properties, 'PLAYBACK::');
  }

  static sendAdMidpoint(props) {
    const properties = Object.assign({
      play_uuid: props.play_uuid,
      content_id: props.content_id,
      content_type: props.content_type,
      ad_uuid: props.ad_uuid,
      ad_tag: props.ad_tag
    }, this.env());

    this.sendEvent('AD::MIDPOINT', properties, 'PLAYBACK::');
  }

  static sendAdThirdQuartile(props) {
    const properties = Object.assign({
      play_uuid: props.play_uuid,
      content_id: props.content_id,
      content_type: props.content_type,
      ad_uuid: props.ad_uuid,
      ad_tag: props.ad_tag
    }, this.env());

    this.sendEvent('AD::THIRD_QUARTILE', properties, 'PLAYBACK::');
  }

  static sendMeta(video_id, level, levels, target_bitrate, play_uuid, genre_id) {
    const props = Object.assign({
      video_id,
      level,
      levels,
      target_bitrate,
      play_uuid,
      genre_id
    }, this.env());

    this.sendEvent('META', props);
  }

  static sendPlatformSupportEvent(options = {}){

    var properties = Object.assign({
      supported: PlayerBrowserSupport.isSupported(),
      video: VideoAdapter.isSupported(),
      h264: VideoAdapter.isSupportH264Encoding(),
      mse: MSEAdapter.isSupported(),
      mse_h264: VideoAdapter.isSupportMSEH264Encoding(),
      webm_vp8: VideoAdapter.isSupportWebmVP8Encoding(),
      mse_vp9: VideoAdapter.isSupportMSEVP9Encoding(),
      vjs_html5: VideoJsAdapter.isHtml5Supported(),
      vjs_mpegurl: VideoJsAdapter.isMpegUrlSupported(),
      type: options.type,
      play_uuid: options.play_uuid
    }, this.env());

    this.sendEvent("PLATFORM::SUPPORT", properties, "PLAYBACK::");
  }

  static sendUnmuteButtonLoad(play_uuid, videoId, videoDuration, hasAd, autoplay){
    var properties = Object.assign({
      play_uuid: play_uuid,
      video_id: videoId,
      video_duration: videoDuration,
      has_ad: hasAd,
      visitor_id: AhoyAdapter.global().visitorId,
      setup_time: Timing.time("vjs-setup"),

      vjs_html5: VideoJsAdapter.isHtml5Supported(),
      vjs_mpegurl: VideoJsAdapter.isMpegUrlSupported(),
    }, this.autoplayAttribute(autoplay), this.env());
    this.sendEvent("UNMUTE::LOAD", properties);
  }

  static sendUnmuteButtonClick(play_uuid, videoId, videoDuration, hasAd, autoplay){
    var properties = Object.assign({
      play_uuid: play_uuid,
      video_id: videoId,
      video_duration: videoDuration,
      has_ad: hasAd,
      visitor_id: AhoyAdapter.global().visitorId,
      setup_time: Timing.time("vjs-setup"),

      vjs_html5: VideoJsAdapter.isHtml5Supported(),
      vjs_mpegurl: VideoJsAdapter.isMpegUrlSupported(),
    }, this.autoplayAttribute(autoplay), this.env());
    this.sendEvent("UNMUTE::CLICK", properties);
  }

  static sendSuggestionCountdown(play_uuid, video_id) {
    const props = Object.assign({
      play_uuid,
      video_id,
      action: 'countdown',
      platform: PlayerEnvironment.platform()
    }, this.env());

    this.sendEvent('PLAYBACK::SUGGESTION', props, '');
  }

  static sendGeoblock(contentType, contentId, play_uuid){
    var properties = Object.assign({
      id: contentId,
      play_uuid: play_uuid
    }, this.env());
    this.sendEvent(contentType.toUpperCase() + '::GEOBLOCKED', properties, 'PLAYBACK::');
  }

  static sendPlay(video_id, position, duration, play_uuid, bandwidth, quality, network_state, has_ad, video_duration, autoplay, video_source_bitrate, video_source_height, video_source_width, player_height, player_width) {
    const props = Object.assign({
      video_id,
      position,
      duration,
      play_uuid,
      bandwidth,
      quality,
      network_state,
      has_ad,
      video_duration,
      video_start_time: ( window.playback.startTime || null ),
      restart_count: ( window.playback.restartCount || 0),
      video_source_bitrate,
      video_source_height,
      video_source_width,
      player_height,
      player_width
    }, this.autoplayAttribute(autoplay), this.env());

    this.sendEvent('WATCH', props);
  }

  static sendEpisodeList({ play_uuid, current_video_id, next_video_id, action } = {}) {
    const props = Object.assign({
      play_uuid,
      current_video_id,
      next_video_id,
      action
    }, this.env());

    this.sendEvent('PLAYBACK::EPISODELIST', props, '')
  }

  static sendSubtitle({ action, subtitle, video_id, play_uuid } = {}) {
    const props = Object.assign({
      action,
      subtitle,
      video_id,
      play_uuid
    }, this.env());

    this.sendEvent('SUBTITLE', props, 'PLAYBACK::')
  }

  static sendPlaybackAd(props) {
    this.sendEvent('AD', Object.assign({}, props, this.env()), 'PLAYBACK::');
  }

  static sendPlaybackBannerAd(props = {}) {
    const sentProps = {
      ad_type: 'banner',
      ad_slot: props.ad_slot,
      ad_event: props.ad_event,
      ad_uuid: props.ad_uuid,
      play_uuid: props.play_uuid,
      advertiser_id: props.advertiser_id,
      campaign_id: props.campaign_id,
      creative_id: props.creative_id,
      is_empty: props.is_empty,
      line_item_id: props.line_item_id,
      size: props.size,
      source_agnostic_creative_id: props.source_agnostic_creative_id,
      source_agnostic_line_Item_id: props.source_agnostic_line_Item_id
    };

    for (let key in sentProps) {
      if (sentProps[key] === undefined) {
        delete sentProps[key];
      }
    }

    this.sendPlaybackAd(sentProps);
  }
}
